// src/components/lesson/Flashcards.tsx
//
// Side-mounted flashcard browser. Click a card to flip front <-> back.
// Includes 3 download formats:
//   - CSV (universal, works with Anki / Quizlet / spreadsheets)
//   - Anki-importable text (front\tback per line, like Anki's "tab-separated")
//   - Printable PDF (1-page cheat sheet, generated client-side via jsPDF)

'use client'

import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import { ChevronLeft, ChevronRight, Download, RotateCw } from 'lucide-react'
import type { Flashcard } from '@/lib/study-plan/lesson-generation'
import { Button } from '@/components/ui/button'
import { normalizeMathMarkdown } from '@/lib/render/normalize-math-markdown'
import { stripMarkdown } from '@/lib/render/strip-markdown'

export default function Flashcards({
  cards,
  topic,
  lessonSlug,
  isAuthenticated,
}: {
  cards: Flashcard[]
  topic: string
  /** Lesson slug — required for spaced-repetition review tracking. */
  lessonSlug: string
  /** When true, the Again/Hard/Good/Easy buttons hit the review API. */
  isAuthenticated: boolean
}) {
  const [index, setIndex] = useState(0)
  const [flipped, setFlipped] = useState(false)
  const [downloadOpen, setDownloadOpen] = useState(false)
  const [feedback, setFeedback] = useState<{
    cardIndex: number
    interval: number
  } | null>(null)

  if (cards.length === 0) return null

  const card = cards[index]

  /**
   * Submit a review rating (Again / Hard / Good / Easy). For
   * authenticated users, hits /api/learn/review/answer to persist
   * SM-2 state. For anonymous users, the buttons advance the card
   * but don't track. This is by design — gives anon users a sense
   * of how the system works, encourages sign-up.
   */
  async function review(difficulty: 'again' | 'hard' | 'good' | 'easy') {
    const reviewedIndex = index
    if (isAuthenticated) {
      try {
        const res = await fetch('/api/learn/review/answer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-store',
          body: JSON.stringify({ lessonSlug, cardIndex: reviewedIndex, difficulty }),
        })
        if (res.ok) {
          const data = (await res.json()) as { interval?: number }
          if (typeof data.interval === 'number') {
            setFeedback({ cardIndex: reviewedIndex, interval: data.interval })
            // Clear feedback after a moment
            setTimeout(() => {
              setFeedback((cur) => (cur && cur.cardIndex === reviewedIndex ? null : cur))
            }, 2500)
          }
        }
      } catch {
        // ignore — never block the UX on tracking
      }
    }
    // Always advance — works for anon and authed
    setFlipped(false)
    setIndex((i) => (i + 1) % cards.length)
  }

  const next = () => {
    setFlipped(false)
    setIndex((i) => (i + 1) % cards.length)
  }
  const prev = () => {
    setFlipped(false)
    setIndex((i) => (i - 1 + cards.length) % cards.length)
  }
  const flip = () => setFlipped((f) => !f)

  const safeTopic = topic.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-')

  const downloadCsv = () => {
    const csv =
      'Front,Back\n' +
      cards
        .map((c) => `"${c.front.replace(/"/g, '""')}","${c.back.replace(/"/g, '""')}"`)
        .join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${safeTopic}-flashcards.csv`
    a.click()
    URL.revokeObjectURL(url)
    setDownloadOpen(false)
  }

  const downloadAnki = () => {
    // Anki "tab-separated values" format — paste into Anki's File → Import.
    const txt = cards.map((c) => `${oneLine(c.front)}\t${oneLine(c.back)}`).join('\n')
    const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${safeTopic}-flashcards-anki.txt`
    a.click()
    URL.revokeObjectURL(url)
    setDownloadOpen(false)
  }

  const downloadPdf = async () => {
    const { jsPDF } = await import('jspdf')
    const doc = new jsPDF({ unit: 'pt', format: 'letter' })
    const margin = 40
    let y = margin
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text(`${topic} — Flashcards`, margin, y)
    y += 22
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')

    cards.forEach((c, i) => {
      // Estimate space; new page if needed
      if (y > 720) {
        doc.addPage()
        y = margin
      }
      doc.setFont('helvetica', 'bold')
      doc.text(`${i + 1}. ${oneLine(c.front)}`, margin, y)
      y += 14
      doc.setFont('helvetica', 'normal')
      const wrapped = doc.splitTextToSize(stripMd(c.back), 540 - margin)
      doc.text(wrapped, margin + 12, y)
      y += wrapped.length * 12 + 10
    })
    doc.save(`${safeTopic}-flashcards.pdf`)
    setDownloadOpen(false)
  }

  return (
    <aside className="rounded-xl border border-border/60 bg-card/40 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Flashcards</h3>
        <div className="relative">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDownloadOpen((o) => !o)}
            className="h-7 text-xs"
            aria-label="Download flashcards"
          >
            <Download className="mr-1 h-3 w-3" />
            Download
          </Button>
          {downloadOpen && (
            <div
              className="absolute right-0 top-8 z-10 w-48 rounded-md border border-border bg-card p-1 shadow-lg"
              onMouseLeave={() => setDownloadOpen(false)}
            >
              <button
                onClick={downloadCsv}
                className="block w-full rounded px-3 py-1.5 text-left text-xs text-foreground hover:bg-muted"
              >
                CSV (Excel / Quizlet)
              </button>
              <button
                onClick={downloadAnki}
                className="block w-full rounded px-3 py-1.5 text-left text-xs text-foreground hover:bg-muted"
              >
                Anki (tab-separated)
              </button>
              <button
                onClick={downloadPdf}
                className="block w-full rounded px-3 py-1.5 text-left text-xs text-foreground hover:bg-muted"
              >
                Printable PDF
              </button>
            </div>
          )}
        </div>
      </div>

      <p className="mt-1 text-xs text-muted-foreground">
        Tap to flip · {index + 1} of {cards.length}
      </p>

      <button
        onClick={flip}
        className={
          'mt-3 flex min-h-[160px] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 text-center transition-colors ' +
          (flipped
            ? 'border-brand/40 bg-brand/10 dark:bg-brand/20'
            : 'border-border bg-background/40 hover:border-border')
        }
        aria-label={flipped ? 'Show front of card' : 'Show back of card'}
      >
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {flipped ? 'Back' : 'Front'}
        </div>
        <div className="prose dark:prose-invert prose-sm max-w-none text-foreground">
          <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
            {normalizeMathMarkdown(flipped ? card.back : card.front)}
          </ReactMarkdown>
        </div>
      </button>

      <div className="mt-3 flex items-center justify-between">
        <Button size="sm" variant="ghost" onClick={prev} aria-label="Previous card" className="h-8">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={flip} className="h-8 text-xs">
          <RotateCw className="mr-1 h-3 w-3" /> Flip
        </Button>
        <Button size="sm" variant="ghost" onClick={next} aria-label="Next card" className="h-8">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* SM-2 review buttons — only after the card is flipped (you've seen the answer) */}
      {flipped && (
        <div className="mt-3 border-t border-border/60 pt-3">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            How well did you know it?
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            <button
              type="button"
              onClick={() => void review('again')}
              className="rounded border border-rose-500/40 bg-rose-500/10 py-1.5 text-[11px] font-medium text-rose-800 dark:text-rose-200 transition-colors hover:bg-rose-500/20"
            >
              Again
            </button>
            <button
              type="button"
              onClick={() => void review('hard')}
              className="rounded border border-amber-500/40 bg-amber-500/10 py-1.5 text-[11px] font-medium text-amber-800 dark:text-amber-200 transition-colors hover:bg-amber-500/20"
            >
              Hard
            </button>
            <button
              type="button"
              onClick={() => void review('good')}
              className="rounded border border-brand/40 bg-brand/10 py-1.5 text-[11px] font-medium text-brand-ink transition-colors hover:bg-brand/20"
            >
              Good
            </button>
            <button
              type="button"
              onClick={() => void review('easy')}
              className="rounded border border-emerald-500/40 bg-emerald-500/10 py-1.5 text-[11px] font-medium text-emerald-800 dark:text-emerald-200 transition-colors hover:bg-emerald-500/20"
            >
              Easy
            </button>
          </div>
          {!isAuthenticated && (
            <p className="mt-1.5 text-[10px] text-muted-foreground">
              Sign in to save reviews and unlock spaced repetition.
            </p>
          )}
          {isAuthenticated && feedback && (
            <p className="mt-1.5 text-[10px] text-emerald-700 dark:text-emerald-300">
              ✓ Reviewing again in {feedback.interval} day{feedback.interval === 1 ? '' : 's'}
            </p>
          )}
        </div>
      )}
    </aside>
  )
}

/** Collapse a multi-line string to one line for tab-separated formats. */
function oneLine(s: string): string {
  return s.replace(/\s+/g, ' ').trim()
}

/** Crude markdown stripping for PDF generation. Keeps text readable. */
function stripMd(s: string): string {
  return stripMarkdown(s)
}
